import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Ticket, FileText, FileSpreadsheet, Ban, Plus } from "lucide-react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";

export default function CouponsTab() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const emptyForm = {
    buyer_name: "", buyer_address: "", buyer_phone: "", buyer_email: "",
    business_type: "", count: 100,
    service_scope: "any", discount_type: "free", discount_value: 0,
    expiry_date: "", campaign_type: "gift",
    voucher_label: "", voucher_tagline: "",
    notes: "",
  };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/coupon-batches");
      setBatches(res.data);
    } catch {
      toast.error("Failed to load coupon batches");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.expiry_date) { toast.error("Set an expiry date"); return; }
    setSubmitting(true);
    try {
      const payload = { ...form, count: Number(form.count), discount_value: Number(form.discount_value || 0) };
      const res = await api.post("/admin/coupon-batches", payload);
      toast.success(`Created ${res.data.generated} coupons for ${res.data.batch.buyer_name}`);
      setOpen(false); setForm(emptyForm); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create batch");
    } finally { setSubmitting(false); }
  };

  const downloadFile = async (path, filename) => {
    try {
      const token = localStorage.getItem("vc_token");
      const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const revoke = async (batch) => {
    if (!window.confirm(`Revoke all remaining coupons for ${batch.buyer_name}?`)) return;
    try {
      const res = await api.post(`/admin/coupon-batches/${batch.batch_id}/revoke`);
      toast.success(`${res.data.revoked} coupon(s) revoked`);
      load();
    } catch { toast.error("Failed"); }
  };

  const scopeLabel = (s) => s === "any" ? "Any vehicle" : s.replace("_", " ");
  const discLabel = (b) => b.discount_type === "free" ? "COMPLIMENTARY PUC" :
    b.discount_type === "percent" ? `${b.discount_value}% OFF` :
    `₹${b.discount_value} OFF`;
  const campaignBadge = (b) => (b.campaign_type || "gift").toUpperCase();

  return (
    <div className="mt-6" data-testid="coupons-tab">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-primary mb-1">Gift vouchers</div>
          <h2 className="font-display text-2xl font-bold tracking-tight">Coupon batches</h2>
          <p className="text-sm text-muted-foreground mt-1">Sell PUC vouchers in bulk to insurance, lubricant, fleet & corporate clients.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-batch-btn" className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 h-11">
              <Plus className="w-4 h-4 mr-2" /> Create batch
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="create-batch-dialog" className="rounded-2xl border-border bg-card max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">Create a coupon batch</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold">Buyer details</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Buyer name *</Label>
                  <Input data-testid="cb-buyer" required value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} placeholder="Bajaj Insurance" className="rounded-lg bg-secondary border-border h-10 mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Nature of business *</Label>
                  <Input data-testid="cb-biz" required value={form.business_type} onChange={(e) => setForm({ ...form, business_type: e.target.value })} placeholder="Insurance / Lubricant / Fleet…" className="rounded-lg bg-secondary border-border h-10 mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Buyer address *</Label>
                <Input data-testid="cb-address" required value={form.buyer_address} onChange={(e) => setForm({ ...form, buyer_address: e.target.value })} placeholder="Office address for invoicing" className="rounded-lg bg-secondary border-border h-10 mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Contact number *</Label>
                  <Input data-testid="cb-phone" required value={form.buyer_phone} onChange={(e) => setForm({ ...form, buyer_phone: e.target.value })} placeholder="+91 9876543210" className="rounded-lg bg-secondary border-border h-10 mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Email (optional)</Label>
                  <Input data-testid="cb-email" type="email" value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })} placeholder="raj@bajaj.co" className="rounded-lg bg-secondary border-border h-10 mt-1" />
                </div>
              </div>

              <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold pt-1">Voucher setup</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Count *</Label>
                  <Input data-testid="cb-count" type="number" min="1" max="5000" required value={form.count} onChange={(e) => setForm({ ...form, count: e.target.value })} className="rounded-lg bg-secondary border-border h-10 mt-1" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Expiry date *</Label>
                  <Input data-testid="cb-expiry" type="date" required value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="rounded-lg bg-secondary border-border h-10 mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Vehicle scope</Label>
                  <Select value={form.service_scope} onValueChange={(v) => setForm({ ...form, service_scope: v })}>
                    <SelectTrigger data-testid="cb-scope" className="rounded-lg bg-secondary border-border h-10 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="two_wheeler">Two-wheeler</SelectItem>
                      <SelectItem value="three_wheeler">Three-wheeler</SelectItem>
                      <SelectItem value="four_wheeler">Four-wheeler</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Campaign type</Label>
                  <Select value={form.campaign_type} onValueChange={(v) => setForm({ ...form, campaign_type: v })}>
                    <SelectTrigger data-testid="cb-campaign" className="rounded-lg bg-secondary border-border h-10 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gift">Gift</SelectItem>
                      <SelectItem value="promo">Promo</SelectItem>
                      <SelectItem value="loyalty">Loyalty</SelectItem>
                      <SelectItem value="corporate">Corporate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Discount</Label>
                  <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                    <SelectTrigger data-testid="cb-disc-type" className="rounded-lg bg-secondary border-border h-10 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">100% (Complimentary)</SelectItem>
                      <SelectItem value="percent">% off</SelectItem>
                      <SelectItem value="flat">₹ flat off</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.discount_type !== "free" && (
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Value</Label>
                    <Input data-testid="cb-disc-value" type="number" min="0" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className="rounded-lg bg-secondary border-border h-10 mt-1" placeholder={form.discount_type === "percent" ? "10" : "50"} />
                  </div>
                )}
              </div>

              <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold pt-1">Custom voucher labels (PDF)</div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Voucher headline (optional)</Label>
                <Input data-testid="cb-label" value={form.voucher_label} onChange={(e) => setForm({ ...form, voucher_label: e.target.value })} placeholder="e.g. Bajaj Free PUC Gift · Castrol Fleet Reward" className="rounded-lg bg-secondary border-border h-10 mt-1" />
                <p className="text-[10px] text-muted-foreground mt-1">Leave blank to auto-use "COMPLIMENTARY PUC / X% OFF / ₹Y OFF".</p>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Sub-tagline (optional)</Label>
                <Input data-testid="cb-tagline" value={form.voucher_tagline} onChange={(e) => setForm({ ...form, voucher_tagline: e.target.value })} placeholder="e.g. Fleet drivers only · Redeem before Diwali" className="rounded-lg bg-secondary border-border h-10 mt-1" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Internal notes (optional)</Label>
                <Input data-testid="cb-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Not printed — visible to admin only" className="rounded-lg bg-secondary border-border h-10 mt-1" />
              </div>
              <DialogFooter>
                <Button data-testid="cb-submit" type="submit" disabled={submitting} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                  {submitting ? "Creating…" : `Generate ${form.count || 0} vouchers`}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading…</div>
      ) : batches.length === 0 ? (
        <div className="border border-dashed border-border rounded-2xl p-12 text-center" data-testid="empty-coupons">
          <Ticket className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <h3 className="font-display text-xl font-bold mb-1">No batches yet</h3>
          <p className="text-sm text-muted-foreground">Create your first bulk voucher batch — perfect for insurance, lubricant or fleet partners.</p>
        </div>
      ) : (
        <div className="border border-border bg-card rounded-2xl overflow-hidden">
          <Table data-testid="coupon-batches-table">
            <TableHeader>
              <TableRow className="border-border">
                <TableHead className="text-xs uppercase tracking-[0.15em]">Buyer</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.15em]">Offer</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.15em]">Scope</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.15em]">Issued</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.15em]">Redeemed</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.15em]">Expiry</TableHead>
                <TableHead className="text-xs uppercase tracking-[0.15em] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {batches.map((b) => {
                const total = (b.stats?.issued || 0) + (b.stats?.redeemed || 0) + (b.stats?.expired || 0) + (b.stats?.revoked || 0);
                const redeemed = b.stats?.redeemed || 0;
                const pct = total > 0 ? Math.round((redeemed / total) * 100) : 0;
                return (
                  <TableRow key={b.batch_id} data-testid={`batch-row-${b.batch_id}`} className="border-border">
                    <TableCell>
                      <div className="font-medium">{b.buyer_name}</div>
                      <div className="text-xs text-muted-foreground">{b.business_type || "—"}</div>
                      {(b.buyer_phone || b.buyer_email) && (
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          {b.buyer_phone && <span>{b.buyer_phone}</span>}
                          {b.buyer_phone && b.buyer_email && <span> · </span>}
                          {b.buyer_email && <span>{b.buyer_email}</span>}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className="rounded-lg bg-primary/10 text-primary border-primary/30">{discLabel(b)}</Badge>
                      <div className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wide">{campaignBadge(b)}</div>
                    </TableCell>
                    <TableCell className="text-sm">{scopeLabel(b.service_scope)}</TableCell>
                    <TableCell className="font-mono">{b.count}</TableCell>
                    <TableCell>
                      <div className="font-mono text-primary">{redeemed}/{total} ({pct}%)</div>
                      {b.stats?.revoked > 0 && <div className="text-[10px] text-red-600">{b.stats.revoked} revoked</div>}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{b.expiry_date}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button data-testid={`dl-pdf-${b.batch_id}`} size="sm" variant="outline" onClick={() => downloadFile(`/admin/coupon-batches/${b.batch_id}/download.pdf`, `coupons_${b.buyer_name.replace(/\s+/g, "_")}.pdf`)} className="rounded-lg border-border h-8">
                        <FileText className="w-3.5 h-3.5 mr-1 text-red-600" /> PDF
                      </Button>
                      <Button data-testid={`dl-csv-${b.batch_id}`} size="sm" variant="outline" onClick={() => downloadFile(`/admin/coupon-batches/${b.batch_id}/download.csv`, `coupons_${b.buyer_name.replace(/\s+/g, "_")}.csv`)} className="rounded-lg border-border h-8">
                        <FileSpreadsheet className="w-3.5 h-3.5 mr-1 text-emerald-600" /> CSV
                      </Button>
                      <Button data-testid={`revoke-${b.batch_id}`} size="sm" variant="outline" onClick={() => revoke(b)} className="rounded-lg border-border h-8">
                        <Ban className="w-3.5 h-3.5 mr-1 text-red-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
